import { useEffect, useSyncExternalStore } from 'react';
import { probeEImzoVersion } from './useEIMZO';

// Ambient E-IMZO availability, shared app-wide without a provider: every
// status dot subscribes to this one module-level store, so the desktop
// client is probed once — not once per button.
//
// status: 'unknown' | 'checking' | 'available' | 'unavailable'

let state = { status: 'unknown', version: null, checkedAt: 0 };
const listeners = new Set();
let inflight = null;

const emit = (next) => {
    state = { ...state, ...next, checkedAt: Date.now() };
    listeners.forEach((l) => l());
};

export const getEImzoAvailability = () => state;

/**
 * Probe the client (deduped — concurrent callers share one probe).
 * `force` re-probes even when a result already exists.
 */
export const checkEImzoAvailability = ({ force = false } = {}) => {
    if (inflight) return inflight;
    if (!force && (state.status === 'available' || state.status === 'unavailable')) {
        return Promise.resolve(state);
    }
    emit({ status: 'checking' });
    inflight = probeEImzoVersion()
        .then((version) => emit({ status: 'available', version }))
        .catch(() => emit({ status: 'unavailable', version: null }))
        .finally(() => {
            inflight = null;
        });
    return inflight;
};

/**
 * Flows that already talked to the client (setup modal, signing panel
 * connect) push their fresher result here so the dots update instantly.
 */
export const reportEImzoAvailability = (ok, version = null) => {
    emit({ status: ok ? 'available' : 'unavailable', version: ok ? version : null });
};

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// The user installs E-IMZO, tabs back — recheck a stale negative result so
// the dot flips green without a reload.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (
            document.visibilityState === 'visible' &&
            state.status === 'unavailable' &&
            Date.now() - state.checkedAt > 5000
        ) {
            checkEImzoAvailability({ force: true });
        }
    });
}

export const useEImzoAvailability = () => {
    const snapshot = useSyncExternalStore(subscribe, getEImzoAvailability);
    useEffect(() => {
        if (snapshot.status === 'unknown') checkEImzoAvailability();
    }, [snapshot.status]);
    return snapshot;
};
